// src/features/help/helpConstants.ts

/** Help center taxonomy — fixed keys; articles declare `category:` from this list. */
export const HELP_CATEGORIES = [
  { key: 'getting-started', title: 'Getting Started' },
  { key: 'videos', title: 'Videos & Uploading' },
  { key: 'streams', title: 'Live Streaming' },
  { key: 'playlists', title: 'Playlists' },
  { key: 'players', title: 'Players & Embedding' },
  { key: 'feeds', title: 'Feeds & Syndication' },
  { key: 'apps', title: 'Apps' },
  { key: 'analytics', title: 'Analytics' },
  { key: 'account', title: 'Account & Team' },
  { key: 'troubleshooting', title: 'Troubleshooting' },
] as const;
export type HelpCategoryKey = (typeof HELP_CATEGORIES)[number]['key'];

/** `requires:` frontmatter values — each maps to a navbar-equivalent boolean. */
export const HELP_REQUIRES = [
  'all',
  'media',
  'ingest',
  'account',
  'billing',
  'sub-accounts',
  'captions',
] as const;
export type HelpRequires = (typeof HELP_REQUIRES)[number];

export const HELP_BRAND_KEYS = ['direct', 'reseller'] as const;
export type HelpBrandKey = (typeof HELP_BRAND_KEYS)[number];

/** Tokens allowed in article bodies; anything else {{like-this}} fails invariants. */
export const HELP_TOKENS = ['brandName', 'operatorName', 'supportContact'] as const;

/** Route roots `routes:` prefixes must start under (invariant sanity check). */
export const HELP_KNOWN_ROUTE_ROOTS = [
  '/',
  '/upload',
  '/videos',
  '/playlists',
  '/streams',
  '/players',
  '/feeds',
  '/apps',
  '/analytics',
  '/legacy-reports',
  '/account',
  '/help',
  '/captions',
] as const;

export const HELP_SUPPORT_EMAIL = 'support@streamhoster.com';
export const HELP_DIRECT_BRAND_NAME = 'Streamhoster';

/**
 * Content lint rules enforced by vitest invariants (plan §5.8/§6.4).
 * Extending an exclusion is a data edit here — never a code change.
 */
export class HelpLintRules {
  /** Internals/stack/infra vocabulary — never allowed in any article body. */
  static readonly FORBIDDEN_TERMS: readonly string[] = [
    'wowza',
    'scala',
    'cloudfront',
    'aws',
    's3',
    'nginx',
    'postgres',
    'mysql',
    'database',
    'backend',
    'java',
    'mantine',
    'tanstack',
    'vite',
    'zustand',
    'react',
  ];

  /**
   * Cost/pricing vocabulary — allowed only in articles gated
   * `requires: billing` + `brands: [direct]` (or via EXEMPTIONS).
   */
  static readonly COST_TERMS: readonly string[] = [
    'price',
    'prices',
    'pricing',
    'cost',
    'costs',
    'billing',
    'invoice',
    'invoices',
    'charge',
    'charges',
    'charged',
    'fee',
    'fees',
    'plan',
    'plans',
    'payment',
    'payments',
    'upgrade',
    'upgrades',
  ];

  /** Cost literals matched verbatim (no word boundary applies). */
  static readonly COST_LITERALS: readonly string[] = ['$'];

  /** Sub-account vocabulary — allowed only in `requires: sub-accounts` articles. */
  static readonly SUBACCOUNT_PATTERN = /\bsub-?accounts?\b/i;

  /** Brand literals — articles must use {{brandName}}; see invariants for the
      narrow direct-only exemption. */
  static readonly BRAND_PATTERN = /streamhoster|themediacdn/i;

  /**
   * Per-article lint exemptions: slug → terms permitted despite containment.
   * Example: the team article names the on-screen "Manage Billing" permission.
   */
  static readonly EXEMPTIONS: Readonly<Record<string, readonly string[]>> = {
    'team-members': ['billing'],
  };
}
